/**
 * Structured JSON logging utility with correlation IDs and org context
 */

import { generateCorrelationId, getSessionCorrelationId } from './correlationId';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface LogContext {
  correlation_id?: string;
  org_id?: string;
  user_id?: string;
  session_id?: string;
  request_id?: string;
  [key: string]: unknown;
}

interface StructuredLogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  correlation_id: string;
  session_id: string;
  org_id?: string;
  user_id?: string;
  context?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  performance?: {
    duration_ms?: number;
    memory_mb?: number;
  };
  request?: {
    method?: string;
    url?: string;
    user_agent?: string;
  };
}

class StructuredLogger {
  private defaultContext: Partial<LogContext> = {};

  setContext(context: Partial<LogContext>) {
    this.defaultContext = { ...this.defaultContext, ...context };
  }

  clearContext() {
    this.defaultContext = {};
  }

  private formatEntry(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ): StructuredLogEntry {
    const entry: StructuredLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      correlation_id: context?.correlation_id || this.defaultContext.correlation_id || generateCorrelationId('log'),
      session_id: context?.session_id || this.defaultContext.session_id || getSessionCorrelationId(),
      org_id: context?.org_id || this.defaultContext.org_id,
      user_id: context?.user_id || this.defaultContext.user_id,
    };

    // Add context excluding standard fields
    if (context) {
      const { correlation_id, org_id, user_id, session_id, request_id, ...rest } = context;
      if (Object.keys(rest).length > 0) {
        entry.context = rest;
      }
      if (request_id) {
        entry.context = { ...entry.context, request_id };
      }
    }

    // Add error details if present
    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: import.meta.env.DEV ? error.stack : undefined, // Only include stack in dev
      };
    }

    return entry;
  }

  private log(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    const entry = this.formatEntry(level, message, context, error);
    const jsonLog = JSON.stringify(entry);

    switch (level) {
      case 'debug':
        if (import.meta.env.DEV) console.debug(jsonLog);
        break;
      case 'info':
        console.info(jsonLog);
        break;
      case 'warn':
        console.warn(jsonLog);
        break;
      case 'error':
      case 'fatal':
        console.error(jsonLog);
        break;
    }

    return entry;
  }

  debug(message: string, context?: LogContext) {
    return this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    return this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    return this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: LogContext) {
    return this.log('error', message, context, error);
  }

  fatal(message: string, error?: Error, context?: LogContext) {
    return this.log('fatal', message, context, error);
  }

  // Performance logging helper
  startTimer(operation: string): () => void {
    const startTime = performance.now();
    const correlationId = generateCorrelationId('perf');

    return () => {
      const duration = performance.now() - startTime;
      this.info(`${operation} completed`, {
        correlation_id: correlationId,
        duration_ms: Math.round(duration * 100) / 100,
      } as LogContext);
    };
  }

  // API request logging
  logApiRequest(method: string, url: string, context?: LogContext) {
    return this.info(`API Request: ${method} ${url}`, {
      ...context,
      http_method: method,
      http_url: url,
    });
  }

  logApiResponse(method: string, url: string, status: number, durationMs: number, context?: LogContext) {
    const level: LogLevel = status >= 500 ? 'error' : status >= 400 ? 'warn' : 'info';
    return this.log(level, `API Response: ${method} ${url} - ${status}`, {
      ...context,
      http_method: method,
      http_url: url,
      http_status: status,
      duration_ms: durationMs,
    } as LogContext);
  }

  // Security event logging
  logSecurityEvent(event: string, severity: 'low' | 'medium' | 'high' | 'critical', context?: LogContext) {
    const level: LogLevel = severity === 'critical' ? 'fatal' : severity === 'high' ? 'error' : 'warn';
    return this.log(level, `Security Event: ${event}`, {
      ...context,
      security_event: event,
      severity,
    } as LogContext);
  }
}

// Export singleton instance
export const logger = new StructuredLogger();

// Export class for custom instances
export { StructuredLogger };
export type { LogContext, LogLevel, StructuredLogEntry };
